import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { PHONE_NUMBER, PHONE_NUMBER_DISPLAY, trackPhoneClick } from '../utils/tracking';
import { routeAlternates } from '../data/fleet';

const Header = ({ language, setLanguage, t }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isRo = language === 'ro';
  const prefix = isRo ? '' : '/en';

  const navItems = [
    { labelRo: 'Închirieri Auto', labelEn: 'Car Rentals', path: '/rent-a-car' },
    { labelRo: 'Transferuri', labelEn: 'Transfers', path: '/transfers' },
    { labelRo: 'Tururi Turistice', labelEn: 'Tours', path: '/tours' },
    { labelRo: 'Contact', labelEn: 'Contact', path: '/contact' },
  ];

  const handleNav = (item) => {
    setIsMenuOpen(false);
    if (item.section) {
      if (location.pathname === '/' || location.pathname === '/en' || location.pathname === '/en/') {
        const el = document.getElementById(item.section);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(prefix || '/');
        setTimeout(() => {
          const el = document.getElementById(item.section);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    } else {
      navigate(`${prefix}${item.path}`);
    }
  };

  const goHome = () => {
    setIsMenuOpen(false);
    navigate(prefix || '/');
  };

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'ro' : 'en';
    setLanguage(newLang);
    if (routeAlternates[location.pathname]) { navigate(routeAlternates[location.pathname]); return; }
    const currentPath = location.pathname.replace(/^\/en/, '') || '/';
    navigate(newLang === 'en' ? `/en${currentPath === '/' ? '' : currentPath}` : currentPath);
  };

  const handlePhoneClick = () => {
    trackPhoneClick();
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  const isActive = (path) => {
    const current = location.pathname.replace(/^\/en/, '') || '/';
    return current === path;
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      <nav className="container mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button onClick={goHome} className="font-display text-xl sm:text-2xl font-bold tracking-tight" data-testid="logo-home">
            <span className="text-[#0D1B3E]">MONTRÉON</span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item, i) => (
              <button key={i} onClick={() => handleNav(item)}
                data-testid={`nav-${item.path?.slice(1) || item.section}`}
                className={`font-body text-sm font-medium transition-colors ${
                  isActive(item.path) ? 'text-[#10B981]' : 'text-gray-600 hover:text-[#0D1B3E]'
                }`}>
                {isRo ? item.labelRo : item.labelEn}
              </button>
            ))}

            <button onClick={toggleLanguage} data-testid="lang-toggle"
              className="font-body text-xs font-semibold tracking-wider uppercase px-3 py-1.5 border border-gray-300 text-gray-600 hover:border-[#0D1B3E] hover:text-[#0D1B3E] transition-all rounded">
              {language === 'ro' ? 'EN' : 'RO'}
            </button>

            <a href={`tel:${PHONE_NUMBER}`} onClick={(e) => { e.preventDefault(); handlePhoneClick(); }}
              data-testid="header-phone"
              className="flex items-center space-x-2 font-body text-sm font-semibold text-[#0D1B3E] hover:text-[#10B981] transition-colors">
              <Phone size={18} className="text-[#10B981]" />
              <span>{PHONE_NUMBER_DISPLAY}</span>
            </a>

            <Button onClick={() => navigate(`${prefix}/rent-a-car`)} data-testid="nav-cta"
              className="bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold px-6 py-5 rounded transition-all shadow-md hover:shadow-lg">
              {isRo ? 'Rezervă Acum' : 'Book Now'}
            </Button>
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex items-center space-x-3">
            <a href={`tel:${PHONE_NUMBER}`} onClick={(e) => { e.preventDefault(); handlePhoneClick(); }}
              data-testid="header-phone-mobile"
              className="flex items-center justify-center w-10 h-10 bg-[#10B981] rounded-full text-white">
              <Phone size={18} />
            </a>
            <button onClick={toggleLanguage}
              className="font-body text-xs font-semibold tracking-wider uppercase px-2 py-1 border border-gray-300 text-gray-600 rounded">
              {language === 'ro' ? 'EN' : 'RO'}
            </button>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} data-testid="mobile-menu-toggle" className="text-[#0D1B3E] p-1">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 space-y-1 border-t border-gray-100 bg-white">
            {navItems.map((item, i) => (
              <button key={i} onClick={() => handleNav(item)}
                className={`block w-full text-left font-body text-base font-medium py-3 transition-colors ${
                  isActive(item.path) ? 'text-[#10B981]' : 'text-gray-600 hover:text-[#0D1B3E]'
                }`}>
                {isRo ? item.labelRo : item.labelEn}
              </button>
            ))}
            <a href={`tel:${PHONE_NUMBER}`} onClick={(e) => { e.preventDefault(); handlePhoneClick(); }}
              className="flex items-center space-x-2 text-[#0D1B3E] font-semibold py-3">
              <Phone size={18} className="text-[#10B981]" />
              <span>{PHONE_NUMBER_DISPLAY}</span>
            </a>
            <div className="pt-2">
              <Button onClick={() => { navigate(`${prefix}/rent-a-car`); setIsMenuOpen(false); }}
                className="w-full bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold py-5 rounded transition-all">
                {isRo ? 'Rezervă Acum' : 'Book Now'}
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
